import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminExtAPI } from '../lib/api';
import { BarChart3, TrendingUp, Users, Globe, DollarSign, Mail, Gavel, Loader2, Activity } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminExtAPI.analytics().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;
  if (!data) return <div className="text-center py-20 text-zinc-500">Failed to load analytics</div>;

  const StatCard = ({ icon: Icon, label, value, sub, color = '#00E5FF' }) => (
    <div className="p-5 bg-[#0A0A0A] border border-white/5 rounded-sm">
      <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4" style={{ color }} /><span className="text-[10px] text-zinc-600 uppercase tracking-wider">{label}</span></div>
      <div className="text-2xl font-bold text-white font-['Outfit']">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );

  const BarViz = ({ items, valueKey, labelKey, maxVal }) => {
    const max = maxVal || Math.max(...items.map(i => i[valueKey] || 0), 1);
    return (
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 w-20 shrink-0 truncate">{item[labelKey]}</span>
            <div className="flex-1 h-5 bg-[#050505] rounded-sm overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#00E5FF]/80 to-[#00E5FF]/30 rounded-sm transition-all"
                style={{ width: `${Math.max(2, (item[valueKey] / max) * 100)}%` }} />
            </div>
            <span className="text-xs text-white font-bold w-16 text-right">{typeof item[valueKey] === 'number' && item[valueKey] > 100 ? `$${item[valueKey].toLocaleString()}` : item[valueKey]}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="analytics-page">
      <div className="flex items-center gap-3 mb-8">
        <BarChart3 className="h-6 w-6 text-[#00E5FF]" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">Analytics Dashboard</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Total Revenue" value={`$${data.revenue.total.toLocaleString()}`} sub={`$${data.revenue.monthly.toLocaleString()} this month`} />
        <StatCard icon={Gavel} label="Total Bids" value={data.bids.total.toLocaleString()} sub={`${data.bids.weekly} this week`} color="#FF6B9D" />
        <StatCard icon={Users} label="Total Users" value={data.users.total.toLocaleString()} sub={`+${data.users.new_7d} this week`} color="#00FF94" />
        <StatCard icon={Globe} label="Active Domains" value={data.domains.total.toLocaleString()} sub={`${data.domains.auctions} auctions`} color="#FFD700" />
        <StatCard icon={Mail} label="Emails Queued" value={data.emails.queued} sub={`${data.emails.total} total`} color="#FF9F43" />
        <StatCard icon={Activity} label="Monthly Bids" value={data.bids.monthly.toLocaleString()} sub={`${data.bids.weekly} weekly`} color="#A78BFA" />
      </div>

      <Tabs defaultValue="revenue" className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-white/5 p-1 rounded-sm">
          <TabsTrigger value="revenue" className="rounded-sm text-xs uppercase data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="analytics-tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="bids" className="rounded-sm text-xs uppercase data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="analytics-tab-bids">Bid Activity</TabsTrigger>
          <TabsTrigger value="domains" className="rounded-sm text-xs uppercase data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="analytics-tab-domains">Domains</TabsTrigger>
          <TabsTrigger value="top" className="rounded-sm text-xs uppercase data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="analytics-tab-top">Top Performers</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Daily Revenue (Last 30 Days)</h3>
            {data.revenue.daily.length > 0 ? (
              <BarViz items={data.revenue.daily} valueKey="revenue" labelKey="date" />
            ) : <p className="text-zinc-500 text-sm">No revenue data yet</p>}
          </div>
        </TabsContent>

        <TabsContent value="bids">
          <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Daily Bid Activity (Last 30 Days)</h3>
            {data.bids.daily.length > 0 ? (
              <BarViz items={data.bids.daily} valueKey="bids" labelKey="date" />
            ) : <p className="text-zinc-500 text-sm">No bid activity yet</p>}
          </div>
        </TabsContent>

        <TabsContent value="domains">
          <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Domain Categories</h3>
            {data.domains.categories.length > 0 ? (
              <BarViz items={data.domains.categories} valueKey="count" labelKey="name" />
            ) : <p className="text-zinc-500 text-sm">No category data</p>}
          </div>
        </TabsContent>

        <TabsContent value="top">
          <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Top Performing Domains</h3>
            {data.top_domains.length > 0 ? (
              <div className="space-y-3">
                {data.top_domains.map((d, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-600 w-6">{i + 1}.</span>
                      <span className="text-white font-medium">{d.full_name}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-xs text-zinc-500">{d.bid_count} bids</span>
                      <span className="text-[#00E5FF] font-bold">${d.current_bid?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-zinc-500 text-sm">No bidding activity yet</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
